import { Card } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { CircularProgress } from "@/components/ui/circular-progress"
import { useAuth } from "@/modules/auth/store"
import { useCosmos } from "@/modules/cosmos/stores/use-cosmos-store"
import type { Feedback } from "@/services/api/odin/activities/diagnostic/study-session/update"
import mixpanelClient from "@/services/mixpanel"
import { ArrowDown, ArrowUp } from "lucide-react"
import { useEffect } from "react"

interface DiagnosticFeedbackProps {
  feedback: Feedback
}

export default function DiagnosticFeedback({ feedback }: DiagnosticFeedbackProps) {
  const isApproved = feedback.scorePercentage >= feedback.approvalScore
  const { user } = useAuth()
  const { cosmosKey } = useCosmos()

  useEffect(() => {
    mixpanelClient.activities.end({
      name: `${user.firstName} ${user.lastName}`,
      approved: isApproved,
      endDate: new Date().toISOString(),
      score: feedback.approvalScore,
      email: user.emailAddress,
      organization: cosmosKey,
    })
  }, [])

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6">
      <div className="space-y-6">
        <div className="flex justify-center">
          <CircularProgress size="xxl" progress={feedback.scorePercentage} />
        </div>
        <div className="text-center">
          <h2 className="mb-2 text-lg font-bold sm:text-xl">
            {isApproved ? "¡Increíble! 🎉" : "¡Ya casi! 💪"}
          </h2>
          <p className="sm:text-md text-sm text-neutral-600">
            {isApproved
              ? "¡Aprobaste la actividad! ¡Vamos por más!"
              : `Alcanza un ${feedback.approvalScore}% de efectividad para aprobar la actividad.`}
          </p>
        </div>
      </div>
      <div className="mt-6 flex w-full flex-col gap-6 sm:mt-8 sm:flex-row sm:gap-12">
        <Card className="flex-1 px-4 py-3 shadow-md sm:px-6 sm:py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="flex h-7 w-7 items-center justify-center rounded-md border border-green-600 bg-green-50 sm:h-8 sm:w-8">
                <ArrowUp className="h-4 w-4 text-green-600" strokeWidth={2} />
              </span>
              <span className="text-sm font-medium text-green-600">Puntos positivos</span>
            </div>
            <p className="text-sm text-neutral-600">{feedback.positive}</p>
          </div>
        </Card>
        <Card className="flex-1 px-4 py-3 shadow-md sm:px-6 sm:py-4">
          <div className="space-y-2">
            <div className="flex items-center gap-3 sm:gap-4">
              <span className="flex h-7 w-7 items-center justify-center rounded-md border border-red-600 bg-red-50 sm:h-8 sm:w-8">
                <ArrowDown className="h-4 w-4 text-red-600" strokeWidth={2} />
              </span>
              <span className="text-sm font-medium text-red-600">Puntos negativos</span>
            </div>
            <p className="text-sm text-neutral-600">{feedback.negative}</p>
          </div>
        </Card>
      </div>
      <div className="mt-6 sm:mt-8">
        <h3 className="text-md mb-3 font-semibold sm:mb-4">¿Qué podés mejorar?</h3>
        <ul className="space-y-3 sm:space-y-4">
          {feedback?.bullets?.map((point, index) => (
            <li key={index} className="flex items-start gap-2 sm:gap-3">
              <Checkbox id={`feedback-${index}`} checked className="mt-0.5 sm:mt-1" />
              <label
                htmlFor={`feedback-${index}`}
                className="cursor-pointer text-sm text-neutral-600"
              >
                {point}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
